import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { DisclosureMemory } from '../state/suggestion-disclosure-state.ts';

type Props = {
  title: string;
  attentionKey?: string;
  busy?: boolean;
  failed?: boolean;
  status?: string;
  memory?: DisclosureMemory;
  children: ReactNode;
};

export function SuggestionDisclosure(props: Props) {
  return <ScopedDisclosure key={props.memory?.key} {...props} />;
}

/** Keep proposals and edits mounted; only a new, actionable proposal opens itself. */
function ScopedDisclosure({ title, attentionKey = '', busy = false, failed = false, status, memory, children }: Props) {
  const { t } = useTranslation();
  const id = useId();
  const [restored] = useState(() => memory?.read());
  const [open, setOpen] = useState(restored?.open ?? Boolean(attentionKey));
  const seen = useRef(new Set(restored?.seen ?? attentionKey.split(':').filter(Boolean)));
  useEffect(() => {
    const ids = attentionKey.split(':').filter(Boolean);
    const hasNewProposal = ids.some((id) => !seen.current.has(id));
    ids.forEach((id) => seen.current.add(id));
    if (hasNewProposal) setOpen(true);
  }, [attentionKey]);
  useEffect(() => {
    memory?.write({ open, seen: [...seen.current] });
  }, [memory, open, attentionKey]);
  return (
    <section className="mb-2 min-w-0 rounded border border-[color:var(--color-line)]">
      <h3 className="m-0">
        <button
          type="button"
          className="flex min-h-11 w-full items-center gap-2 rounded px-3 py-2 text-left text-sm focus-visible:outline-2 focus-visible:outline-offset-2"
          aria-expanded={open}
          aria-controls={id}
          onClick={(event) => {
            event.currentTarget.focus();
            setOpen((value) => !value);
          }}
        >
          <span aria-hidden="true">{open ? '▾' : '▸'}</span>
          <span className="min-w-0 flex-1 font-semibold">{title}</span>
          {status || busy || failed || attentionKey ? (
            <span className="text-xs text-[color:var(--color-muted)]" role="status">
              {status ??
                t(
                  busy
                    ? 'suggestionDisclosure.working'
                    : failed
                      ? 'suggestionDisclosure.unavailable'
                      : 'suggestionDisclosure.review',
                )}
            </span>
          ) : null}
        </button>
      </h3>
      <div id={id} hidden={!open} className="border-t border-[color:var(--color-line)] p-3">
        {children}
      </div>
    </section>
  );
}
